import clsx from "clsx";
import { useState } from "react";
import { Button, Checkbox, NumberField, Tooltip } from "@/components";
import { formatInteger, pluralize } from "@/lib/format";
import type { FacetCount } from "../api";
import { facetValueLabel, rangeFromParam, unitLabel, valuesFromParam } from "../format";
import styles from "./FacetPanel.module.css";

const SHOWN_VALUES = 6;

interface FacetPanelProps {
  facets: FacetCount[];
  params: URLSearchParams;
  /** Set a facet's filter param ("5..15", "ptfe,pe_cone") or clear it (null). */
  onFilterChange: (key: string, value: string | null) => void;
  onUnknownChange: (key: string, include: boolean) => void;
}

/** The filters of the category: ranges for numbers, values with counts for the rest, and how many
 * items don't state each facet (kept out of a filter unless asked for). */
export function FacetPanel({ facets, params, onFilterChange, onUnknownChange }: FacetPanelProps) {
  const shown = facets.filter((facet) => facet.known > 0 || params.get(facet.key));
  if (shown.length === 0) return null;
  return (
    <div className={styles.panel} role="group" aria-label="Filters">
      {shown.map((facet) => (
        <FacetGroup
          key={facet.key}
          facet={facet}
          param={params.get(facet.key)}
          onFilterChange={onFilterChange}
          onUnknownChange={onUnknownChange}
        />
      ))}
    </div>
  );
}

interface FacetGroupProps {
  facet: FacetCount;
  param: string | null;
  onFilterChange: FacetPanelProps["onFilterChange"];
  onUnknownChange: FacetPanelProps["onUnknownChange"];
}

function FacetGroup({ facet, param, onFilterChange, onUnknownChange }: FacetGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const active = Boolean(param);
  const heading = (
    <span className={styles.heading}>
      <span className={styles.label}>{facet.label}</span>
      {facet.help ? (
        <Tooltip content={facet.help}>
          <button type="button" className={styles.help} aria-label={`About ${facet.label}`}>
            ?
          </button>
        </Tooltip>
      ) : null}
    </span>
  );
  return (
    <fieldset className={styles.group} data-active={active || undefined}>
      <legend className={styles.legend}>
        {heading}
        {active ? (
          <Button variant="ghost" size="sm" onClick={() => onFilterChange(facet.key, null)}>
            Clear
          </Button>
        ) : null}
      </legend>
      {facet.kind === "number" ? (
        <RangeInputs facet={facet} param={param} onFilterChange={onFilterChange} />
      ) : (
        <ValueList facet={facet} param={param} expanded={expanded} onFilterChange={onFilterChange} />
      )}
      {facet.kind !== "number" && facet.values.length > SHOWN_VALUES ? (
        <Button variant="ghost" size="sm" onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Show fewer" : `Show all ${formatInteger(facet.values.length)}`}
        </Button>
      ) : null}
      {facet.unknown > 0 ? (
        active ? (
          <Checkbox
            className={styles.unknown}
            label={`Include ${formatInteger(facet.unknown)} that don’t say`}
            checked={facet.include_unknown}
            onCheckedChange={(checked) => onUnknownChange(facet.key, checked)}
          />
        ) : (
          <p className={styles.unknownNote}>{pluralize(facet.unknown, "listing doesn’t say", "listings don’t say")}</p>
        )
      ) : null}
    </fieldset>
  );
}

function RangeInputs({ facet, param, onFilterChange }: Omit<FacetGroupProps, "onUnknownChange">) {
  const { min, max } = rangeFromParam(param);
  const unit = unitLabel(facet.unit);
  const commit = (next: { min: string | null; max: string | null }) => {
    onFilterChange(facet.key, next.min || next.max ? `${next.min ?? ""}..${next.max ?? ""}` : null);
  };
  return (
    <div className={styles.range}>
      <NumberField
        label={`${facet.label} from`}
        hideLabel
        size="sm"
        min={0}
        unit={unit === "pcs" ? undefined : unit}
        placeholder={facet.min ? `${facet.min}` : "Min"}
        value={min}
        onValueCommitted={(value) => commit({ min: value, max })}
      />
      <span className={styles.dash} aria-hidden="true">
        –
      </span>
      <NumberField
        label={`${facet.label} to`}
        hideLabel
        size="sm"
        min={0}
        unit={unit === "pcs" ? undefined : unit}
        placeholder={facet.max ? `${facet.max}` : "Max"}
        value={max}
        onValueCommitted={(value) => commit({ min, max: value })}
      />
    </div>
  );
}

function ValueList({
  facet,
  param,
  expanded,
  onFilterChange,
}: Omit<FacetGroupProps, "onUnknownChange"> & { expanded: boolean }) {
  const selected = valuesFromParam(param);
  const unlisted = (value: string) => ({ value, label: value, count: 0, add_on_count: 0 });
  const values = facet.values.length ? facet.values : selected.map(unlisted);
  const visible = expanded ? values : values.slice(0, SHOWN_VALUES);
  const extra = selected.filter((value) => !visible.some((option) => option.value === value));
  const toggle = (value: string, checked: boolean) => {
    const next = checked ? [...selected, value] : selected.filter((item) => item !== value);
    onFilterChange(facet.key, next.length ? next.join(",") : null);
  };
  return (
    <ul className={styles.values}>
      {[...visible, ...extra.map(unlisted)].map((option) => (
        <li key={option.value} className={styles.value}>
          <Checkbox
            label={facetValueLabel(facet, option.value, option.label)}
            checked={selected.includes(option.value)}
            onCheckedChange={(checked) => toggle(option.value, checked)}
          />
          {option.add_on_count > 0 ? (
            <Tooltip content={`${formatInteger(option.add_on_count)} of them only as an add-on you order with it`}>
              <button type="button" className={clsx(styles.count, styles.addOnCount)}>
                {formatInteger(option.count)}
              </button>
            </Tooltip>
          ) : (
            <span className={styles.count}>{formatInteger(option.count)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
